import { useMemo } from 'react';
import {
  calculateTimeSpent,
  formatTimeInput,
  parseDurationString,
  parseTimeInput,
} from '../utils';

type EditMode = 'duration' | 'timeRange';

interface TimeCalculationResult {
  calculatedEndTime: string | null;
  calculatedDuration: { error: boolean; seconds: number } | null;
}

/**
 * Shared hook for calculating end time (duration mode) and duration (time range mode).
 * Used by both CreateIssueModal and EditTimeLogModal.
 */
export const useTimeCalculation = (
  editMode: EditMode,
  date: string,
  startTime: string,
  endTime: string,
  durationInput: string,
): TimeCalculationResult => {
  const calculatedEndTime = useMemo(() => {
    if (editMode !== 'duration' || !date || !startTime || !durationInput) return null;
    try {
      const durationSeconds = parseDurationString(durationInput);
      if (durationSeconds === null || durationSeconds <= 0) return null;
      const baseDate = new Date(date);
      if (isNaN(baseDate.getTime())) return null;
      const start = parseTimeInput(startTime, '0', baseDate);
      const end = new Date(start.getTime() + durationSeconds * 1000);
      return formatTimeInput(end);
    } catch {
      return null;
    }
  }, [editMode, date, startTime, durationInput]);

  const calculatedDuration = useMemo(() => {
    if (editMode !== 'timeRange' || !date || !startTime || !endTime) return null;
    try {
      const baseDate = new Date(date);
      if (isNaN(baseDate.getTime())) return null;
      const start = parseTimeInput(startTime, '0', baseDate);
      const end = parseTimeInput(endTime, '0', baseDate);
      if (end <= start) return { error: true, seconds: 0 };
      const seconds = calculateTimeSpent(start, end);
      return { error: false, seconds };
    } catch {
      return null;
    }
  }, [editMode, date, startTime, endTime]);

  return { calculatedEndTime, calculatedDuration };
};
